"use client";

import { useState, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslations, useLocale } from 'next-intl';
import HeaderPage from "@/components/header-page";
import MaisonCard from "@/components/MaisonCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from "@/components/ui/pagination";
import { Card, CardContent } from "@/components/ui/card";
import {
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Home,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const metaData = {
  meta_title_fr: "Maisons d'Hôtes en Tunisie - Séjour Authentique et Convivial",
  meta_description_fr: "Découvrez les meilleures maisons d'hôtes en Tunisie pour un séjour authentique et convivial. Profitez de l'hospitalité tunisienne, des hébergements uniques et des expériences locales mémorables.",
  meta_keywords_fr: "maisons d'hôtes Tunisie, hébergement Tunisie, séjour authentique Tunisie, tourisme Tunisie, maisons d'hôtes traditionnelles, vacances Tunisie, hébergement local Tunisie, expériences tunisiennes",
  meta_title_en: "Guest Houses in Tunisia - Authentic and Cozy Accommodation",
  meta_description_en: "Discover the best guest houses in Tunisia for an authentic and cozy stay. Enjoy Tunisian hospitality, unique accommodations, and unforgettable local experiences across the country.",
  meta_keywords_en: "guest houses Tunisia, accommodation Tunisia, authentic stay Tunisia, Tunisia tourism, traditional guest houses, Tunisia vacation, local lodging Tunisia, Tunisian experiences",
  meta_title_ar: "بيوت الضيافة في تونس - إقامة أصيلة ومريحة",
  meta_description_ar: "اكتشف أفضل بيوت الضيافة في تونس لإقامة أصيلة ومريحة. استمتع بالضيافة التونسية والإقامات الفريدة والتجارب المحلية التي لا تُنسى في جميع أنحاء البلاد.",
  meta_keywords_ar: "بيوت ضيافة تونس, إقامة تونس, إقامة أصيلة تونس, سياحة تونس, بيوت ضيافة تقليدية, عطلات تونس, سكن محلي تونس, تجارب تونسية",
  meta_title_ru: "Гостевые Дома в Тунисе - Аутентичное и Уютное Проживание",
  meta_description_ru: "Откройте для себя лучшие гостевые дома в Тунисе для аутентичного и уютного отдыха. Насладитесь тунисским гостеприимством, уникальным жильём и незабываемыми местными впечатлениями по всей стране.",
  meta_keywords_ru: "гостевые дома Тунис, проживание Тунис, аутентичный отдых Тунис, туризм Тунис, традиционные гостевые дома, отпуск Тунис, местное жильё Тунис, тунисский опыт",
  meta_title_zh: "突尼斯民宿 - 正宗舒适的住宿体验",
  meta_description_zh: "探索突尼斯最佳民宿，享受正宗舒适的住宿体验。体验突尼斯热情好客、独特住宿和难忘的本地文化体验。",
  meta_keywords_zh: "突尼斯民宿, 突尼斯住宿, 正宗住宿 突尼斯, 突尼斯旅游, 传统民宿, 突尼斯度假, 本地住宿 突尼斯, 突尼斯体验",
  meta_title_ja: "チュニジアのゲストハウス - 本格的で快適な宿泊",
  meta_description_ja: "チュニジアの最高のゲストハウスで本格的で快適な滞在を体験してください。チュニジアのおもてなし、ユニークな宿泊施設、忘れられない地元体験をお楽しみください。",
  meta_keywords_ja: "チュニジア ゲストハウス, チュニジア 宿泊, 本格的な滞在 チュニジア, チュニジア観光, 伝統的ゲストハウス, チュニジア休暇, 地元宿泊 チュニジア, チュニジア体験",
  meta_title_ko: "튀니지 게스트하우스 - 진정성 있고 아늑한 숙박",
  meta_description_ko: "튀니지 최고의 게스트하우스에서 진정성 있고 아늑한 숙박을 경험하세요. 튀니지의 환대, 독특한 숙소, 잊지 못할 현지 체험을 즐기세요.",
  meta_keywords_ko: "튀니지 게스트하우스, 튀니지 숙박, 진정한 숙박 튀니지, 튀니지 관광, 전통 게스트하우스, 튀니지 휴가, 현지 숙소 튀니지, 튀니지 체험"
};

/* ---------------- TYPES ---------------- */

interface PaginationLinkType {
  url: string | null;
  label: string;
  active: boolean;
}

interface GuestHouseApiResponse {
  maisons: {
    current_page: number;
    data: any[];
    from: number;
    last_page: number;
    links: PaginationLinkType[];
    next_page_url: string | null;
    prev_page_url: string | null;
    to: number;
    total: number;
  };
}

interface FilterOptions {
  destinations: any[];
}

/* ---------------- COMPONENT ---------------- */

export default function GuestHousesClient() {
  const t = useTranslations();
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const router = useRouter();
  const searchParams = useSearchParams();

  const [guestHouses, setGuestHouses] = useState<any[]>([]);
  const [paginationData, setPaginationData] =
    useState<GuestHouseApiResponse["maisons"] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDestination, setSelectedDestination] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    destinations: [],
  });

  /* ---------------- SEO METADATA ---------------- */

  useEffect(() => {
    const suffix = locale === 'fr' ? 'fr' : locale;
    const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || 'https://tunisiagotravel.com';
    
    // Update title
    document.title = metaData[`meta_title_${suffix}` as keyof typeof metaData] || metaData.meta_title_en;
    
    // Update or create meta description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const description = metaData[`meta_description_${suffix}` as keyof typeof metaData] || metaData.meta_description_en;
    metaDesc.setAttribute('content', description);
    
    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    const keywords = metaData[`meta_keywords_${suffix}` as keyof typeof metaData] || metaData.meta_keywords_en;
    metaKeywords.setAttribute('content', keywords);
    
    // Update or create canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${baseUrl}/${locale}/guest-houses`);
  }, [locale]);

  /* ---------------- URL INIT ---------------- */

  useEffect(() => {
    if (!isInitialLoad) return;

    const page = searchParams.get("page");
    const search = searchParams.get("search");
    const destination = searchParams.get("destination");

    if (page) setCurrentPage(Number(page));
    if (search) setSearchQuery(search);
    if (destination) setSelectedDestination(destination);

    setIsInitialLoad(false);
  }, [searchParams, isInitialLoad]);

  /* ---------------- UPDATE URL ---------------- */

  useEffect(() => {
    if (isInitialLoad) return;

    const params = new URLSearchParams();
    if (currentPage !== 1) params.set("page", String(currentPage));
    if (searchQuery) params.set("search", searchQuery);
    if (selectedDestination !== "all")
      params.set("destination", selectedDestination);

    router.push(
      params.toString()
        ? `/guest-houses?${params.toString()}`
        : "/guest-houses",
      { scroll: false }
    );
  }, [currentPage, searchQuery, selectedDestination]);

  /* ---------------- FETCH FILTERS ---------------- */

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}alldestinations`
        );
        const data = await res.json();
        setFilterOptions({
          destinations: (data.destinations || []).sort((a: any, b: any) =>
            a.name.localeCompare(b.name)
          ),
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchFilters();
  }, []);

  /* ---------------- FETCH DATA ---------------- */

  useEffect(() => {
    if (loadingFilters || isInitialLoad) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(currentPage),
        });
        if (searchQuery) params.set("search", searchQuery);
        if (selectedDestination !== "all")
          params.set("destination_id", selectedDestination);

        const res = await fetch(
          `${process.env.NEXT_PUBLIC_API_URL}maison?${params}`
        );
        const data: GuestHouseApiResponse = await res.json();

        setGuestHouses(data.maisons.data);
        setPaginationData(data.maisons);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentPage, searchQuery, selectedDestination, loadingFilters]);

  /* ---------------- HELPERS ---------------- */

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedDestination("all");
    setCurrentPage(1);
    router.push("/guest-houses", { scroll: false });
  };

  const handlePageChange = (url: string | null) => {
    if (!url) return;
    const page = new URL(url).searchParams.get("page");
    if (page) {
      setCurrentPage(Number(page));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Function to render pagination links
  const renderPaginationLinks = () => {
    if (!paginationData || !paginationData.links) return null;

    return paginationData.links.map((link, index) => {
      if (link.label === "...") {
        return (
          <PaginationItem key={index}>
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const isPrevious = link.label.includes("previous");
      const isNext = link.label.includes("next");
      
      if (isPrevious) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.prev_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.prev_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              ) : (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.previous")}
                  </span>
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (isNext) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(paginationData?.next_page_url || null);
              }}
              className={`gap-2 px-3 sm:px-4 py-2 min-w-[100px] min-h-[44px] flex items-center justify-center ${
                !paginationData?.next_page_url
                  ? "pointer-events-none opacity-50"
                  : ""
              }`}
            >
              {locale === "ar" ? (
                <>
                  <ChevronLeftIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                </>
              ) : (
                <>
                  <span className="hidden sm:block text-xs sm:text-sm">
                    {t("common.next")}
                  </span>
                  <ChevronRightIcon className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                </>
              )}
            </PaginationLink>
          </PaginationItem>
        );
      }

      const pageNum = parseInt(link.label);
      if (!isNaN(pageNum)) {
        return (
          <PaginationItem key={index}>
            <PaginationLink
              href="#"
              onClick={(e) => {
                e.preventDefault();
                handlePageChange(link.url);
              }}
              isActive={link.active}
              className="min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center text-sm sm:text-base"
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        );
      }

      return null;
    });
  };

  const getDestinationName = () =>
    filterOptions.destinations.find(
      (d) => d.slug === selectedDestination
    )?.name;

  /* ---------------- RENDER ---------------- */

  return (
    <div className="min-h-screen" dir={isRTL ? 'rtl' : 'ltr'}>
      <HeaderPage
        title={t('navigation.guestHouses')}
        breadcrumbs={[
          { label: t('common.home'), href: "/" },
          { label: t('navigation.guestHouses'), href: "/guest-houses" },
        ]}
      />

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <Skeleton className="h-64 w-full" />
        ) : guestHouses.length === 0 ? (
          <div className="text-center">
            <Home className="h-16 w-16 mx-auto text-gray-400" />
            <p className="mt-4">{t('common.notFound')}</p>
            <Button onClick={clearFilters} className="mt-4">
              {t('common.back')}
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {guestHouses.map((g) => (
                <MaisonCard key={g.id} item={g} />
              ))}
            </div>

            {paginationData && paginationData.last_page > 1 && (
              <div className="mt-8 sm:mt-12">
                <Pagination className="w-full">
                  <PaginationContent className={`flex flex-wrap justify-center gap-1 sm:gap-2 ${locale === 'ar' ? 'flex-row-reverse' : ''}`}>
                    {renderPaginationLinks()}
                  </PaginationContent>
                </Pagination>
                
                <div className="mt-3 sm:mt-4 text-center text-xs sm:text-sm text-foreground dark:text-gray-400">
                  <p className={locale === 'ar' ? 'font-arabic' : ''}>
                    {t('activities.page')} {paginationData.current_page} {t('activities.of')} {paginationData.last_page}
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
